import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, saveToken, clearToken } from '../services/api';
import { hasPermission } from '../utils/permissions';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        clearToken();
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  // Rechten (toegangsrol) actueel houden: na herladen de laatste rol en permissies ophalen.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authAPI.me()
      .then((fresh) => {
        if (cancelled || !fresh) return;
        setUser((prev) => {
          if (!prev) return prev;
          const merged = {
            ...prev,
            role: fresh.role || prev.role,
            location: fresh.location || prev.location,
            hasAccessRole: !!fresh.hasAccessRole,
            accessRole: fresh.accessRole || null,
            permissions: fresh.permissions || []
          };
          const storage = localStorage.getItem('user') ? localStorage : sessionStorage;
          storage.setItem('user', JSON.stringify(merged));
          return merged;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const login = async (email, password, remember = true) => {
    try {
      const data = await authAPI.login(email, password);

      saveToken(data.token, remember);

      if (remember) {
        localStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.removeItem('user');
      } else {
        sessionStorage.setItem('user', JSON.stringify(data.user));
        localStorage.removeItem('user');
      }

      setToken(data.token);
      setUser(data.user);

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    authAPI.logout().catch(() => {});
    clearToken();
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    const newUser = { ...user, ...updatedUser };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const can = (menuKey, action = 'read') => hasPermission(user, menuKey, action);

  const value = {
    user,
    can,
    token,
    loading,
    login,
    logout,
    updateUser,
    hasRole,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isInstructor: user?.role === 'instructor',
    isStudent: user?.role === 'student'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
